import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Form, Nav, Spinner, Tab } from "react-bootstrap";
import Helmet from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb";
import CustomButton from "../../components/customButton/customButton";
import ConfirmModal from "../../components/confirmModal/confirmModel";
import TicketPhaseBuilder from "../../components/eventPricing/TicketPhaseBuilder";
import TablePriceHikePanel from "../../components/eventPricing/TablePriceHikePanel";
import TableAutoPriceHikePanel from "../../components/eventPricing/TableAutoPriceHikePanel";
import {
  fetchTableBookingLayout,
  fetchTableSheetShareLink,
  fetchPublicTableSheet,
} from "../../api/tableBookingApi";
import {
  buildStaffClubEventQuery,
  getEventClubId,
} from "../../helpers/eventClubScope";
import { pickEventDisplayNumber } from "../../helpers/eventDisplayNumber";
import { buildEventUpdateFromRecord } from "../../helpers/managerEventUpdate";
import {
  flattenLayoutPricingRows,
  normalizeLayoutForEventPricing,
  pickEventTableBookingFromItem,
} from "../../helpers/eventTableBooking";
import {
  indexLiveSheetTables,
  liveStatusKeyFromSheetTable,
  resolveLiveSheetTable,
} from "../../helpers/eventTableBookingLiveStatus";
import {
  unwrapTableSheetResponse,
  unwrapTableSheetShareLink,
} from "../../helpers/tableSheet";
import {
  buildTableBookingPriceHikePayload,
  emptyTableBookingPriceHike,
  isLiveTableBookable,
  pickTableBookingPriceHike,
  tableMatchesHikeScope,
  validateTableBookingPriceHike,
} from "../../helpers/tableBookingPriceHike";
import {
  buildTableBookingAutoPriceHikePayload,
  emptyTableBookingAutoPriceHike,
  pickAutoPriceHikeStatus,
  pickTableBookingAutoPriceHike,
  validateTableBookingAutoPriceHike,
} from "../../helpers/tableBookingAutoPriceHike";
import {
  prepareTicketPhasesForApi,
  validateTicketPhases,
} from "../../helpers/ticketSalePhases";
import { normalizeLayoutDetail } from "../tableBooking/tableBookingUtils";
import useAuth from "../../hooks/useAuth";
import Constant from "../../util/constant";
import {
  currentEventListResponce,
  updateEvent,
} from "../../store/features/event/eventSlice";
import { clubListResponce } from "../../store/features/club/clubSlice";
import { showToast } from "../../store/features/toast/toastSlice";
import "./eventPricingHub.css";

const EventPricingHubContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, managedClubId } = useAuth();
  const eventsRaw = useSelector((s) => s?.event?.currentEventListResponce ?? []);
  const clubList = useSelector((s) => s?.club?.clubList ?? []);
  const [listBusy, setListBusy] = useState(true);

  const [adminClubId, setAdminClubId] = useState("");
  const [eventId, setEventId] = useState(searchParams.get("eventId") || "");
  const [hubTab, setHubTab] = useState("phases");
  const [tickets, setTickets] = useState([]);
  const [hike, setHike] = useState(emptyTableBookingPriceHike());
  const [autoHike, setAutoHike] = useState(emptyTableBookingAutoPriceHike());
  const [layout, setLayout] = useState(null);
  const [layoutLoading, setLayoutLoading] = useState(false);
  const [liveIndex, setLiveIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmKind, setConfirmKind] = useState(null);

  const effectiveClubId = useMemo(() => {
    if (isAdmin && adminClubId) return String(adminClubId);
    if (managedClubId) return String(managedClubId);
    return null;
  }, [isAdmin, adminClubId, managedClubId]);

  const events = useMemo(() => {
    const list = Array.isArray(eventsRaw) ? eventsRaw : [];
    if (!effectiveClubId) return isAdmin ? list : [];
    return list.filter((ev) => getEventClubId(ev) === effectiveClubId);
  }, [eventsRaw, effectiveClubId, isAdmin]);

  const selectedEvent = useMemo(
    () => events.find((ev) => String(ev?._id) === String(eventId)) || null,
    [events, eventId]
  );

  const booking = pickEventTableBookingFromItem(selectedEvent);

  useEffect(() => {
    dispatch(clubListResponce());
  }, [dispatch]);

  useEffect(() => {
    const query = isAdmin
      ? effectiveClubId
        ? { clubid: effectiveClubId }
        : {}
      : buildStaffClubEventQuery(managedClubId);
    if (!isAdmin && !query) {
      setListBusy(false);
      return;
    }
    setListBusy(true);
    dispatch(currentEventListResponce(query || {})).finally(() =>
      setListBusy(false)
    );
  }, [dispatch, isAdmin, effectiveClubId, managedClubId]);

  useEffect(() => {
    if (!selectedEvent) {
      setTickets([]);
      setHike(emptyTableBookingPriceHike());
      setAutoHike(emptyTableBookingAutoPriceHike());
      return;
    }
    setTickets(Array.isArray(selectedEvent.tickets) ? selectedEvent.tickets : []);
    setHike(pickTableBookingPriceHike(selectedEvent));
    setAutoHike(pickTableBookingAutoPriceHike(selectedEvent));
  }, [selectedEvent]);

  useEffect(() => {
    if (eventId) {
      setSearchParams({ eventId }, { replace: true });
    }
  }, [eventId, setSearchParams]);

  useEffect(() => {
    const clubId = getEventClubId(selectedEvent);
    const layoutId = booking.tableBookingLayoutId;
    if (!clubId || !layoutId || !booking.tableBookingEnabled) {
      setLayout(null);
      return;
    }
    let cancelled = false;
    setLayoutLoading(true);
    fetchTableBookingLayout(clubId, layoutId)
      .then((res) => {
        if (cancelled) return;
        setLayout(
          normalizeLayoutForEventPricing(normalizeLayoutDetail(res)) || null
        );
      })
      .catch(() => {
        if (!cancelled) setLayout(null);
      })
      .finally(() => {
        if (!cancelled) setLayoutLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    selectedEvent,
    booking.tableBookingEnabled,
    booking.tableBookingLayoutId,
  ]);

  useEffect(() => {
    const clubId = getEventClubId(selectedEvent);
    const id = selectedEvent?._id;
    if (!clubId || !id || !booking.tableBookingEnabled) {
      setLiveIndex(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const shareRaw = await fetchTableSheetShareLink(clubId);
        if (cancelled) return;
        const share = unwrapTableSheetShareLink(shareRaw);
        const slug = String(share?.slug || "").trim().toLowerCase();
        if (!slug) return;
        const sheetRaw = await fetchPublicTableSheet(slug, { eventId: id });
        if (cancelled) return;
        const payload = unwrapTableSheetResponse(sheetRaw);
        setLiveIndex(indexLiveSheetTables(payload?.sheet || null));
      } catch {
        if (!cancelled) setLiveIndex(null);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedEvent, booking.tableBookingEnabled]);

  const remainingHikeTargets = useMemo(() => {
    if (!hike.enabled || !layout) return 0;
    const rows = flattenLayoutPricingRows(layout).filter((r) => r.kind === "table");
    return rows.filter((row) => {
      if (!tableMatchesHikeScope(row.areaId, row.tableId, hike)) return false;
      const live = resolveLiveSheetTable(row, liveIndex);
      const key = liveStatusKeyFromSheetTable(live);
      return isLiveTableBookable(key);
    }).length;
  }, [hike, layout, liveIndex]);

  const validateAll = useCallback(() => {
    for (const ticket of tickets) {
      const err = validateTicketPhases(ticket, t);
      if (err) return err;
    }
    return (
      validateTableBookingPriceHike(hike, t) ||
      validateTableBookingAutoPriceHike(autoHike, t)
    );
  }, [tickets, hike, autoHike, t]);

  const persist = async () => {
    if (!selectedEvent?._id) return;
    const err = validateAll();
    if (err) {
      dispatch(showToast({ show: false, message: err, type: "error" }));
      return;
    }
    const payload = buildEventUpdateFromRecord(selectedEvent, (base) => ({
      ...base,
      tickets: tickets.map((tk) =>
        prepareTicketPhasesForApi(tk)
      ),
      ...buildTableBookingPriceHikePayload(hike),
      ...buildTableBookingAutoPriceHikePayload(autoHike),
    }));
    if (!payload) return;
    setSaving(true);
    try {
      await dispatch(updateEvent(payload)).unwrap();
      dispatch(
        showToast({
          show: false,
          message: t("EVENT_PRICING_SAVED"),
          type: "success",
        })
      );
      const query = isAdmin
        ? effectiveClubId
          ? { clubid: effectiveClubId }
          : {}
        : buildStaffClubEventQuery(managedClubId);
      dispatch(currentEventListResponce(query || {}));
    } catch (e) {
      dispatch(
        showToast({
          show: false,
          message:
            typeof e === "string" ? e : e?.message || t("EVENT_PRICING_SAVE_ERROR"),
          type: "error",
        })
      );
    } finally {
      setSaving(false);
      setConfirmKind(null);
    }
  };

  const onSaveClick = () => {
    const err = validateAll();
    if (err) {
      dispatch(showToast({ show: false, message: err, type: "error" }));
      return;
    }
    if (autoHike.enabled) {
      setConfirmKind("auto");
      return;
    }
    if (hike.enabled) {
      setConfirmKind("hike");
      return;
    }
    persist();
  };

  const role = window.localStorage.getItem("role");
  const clubStaffShell =
    role === Constant.ROLES.CLUB_MANAGER ? " club-staff-page-shell" : "";

  return (
    <React.Fragment>
      <Helmet>
        <title>Event phases | RizzTix</title>
      </Helmet>
      <div className={`pageContent event-pricing-hub${clubStaffShell}`}>
        <Breadcrumbs
          innerPagetitle={t("EVENT_PHASES_MENU")}
          breadcrumbItem={t("EVENT_PHASES_MENU")}
          pageTitle={[{ title: t("EVENTS") }]}
          BreadcrumbInner
          BreadcrumbmainTitle
        />
        <Card className="mainCard">
          <Card.Body>
            <div className="eph-hero">
              <div className="eph-hero-copy">
                <div className="eph-kicker">{t("EVENT_PRICING_KICKER")}</div>
                <h2>{t("EVENT_PRICING_TITLE")}</h2>
                <p>{t("EVENT_PRICING_INTRO")}</p>
              </div>
              <CustomButton
                className="black-btn Fill_Button eventPrimaryBtn"
                type="button"
                title={t("EVENT_PRICING_SAVE")}
                disabled={!selectedEvent || saving}
                loading={saving}
                onClick={onSaveClick}
              />
            </div>

            <div className={`eph-toolbar${isAdmin ? " is-admin" : ""}`}>
              {isAdmin ? (
                <Form.Group>
                  <Form.Label>{t("CLUBS")}</Form.Label>
                  <Form.Select
                    value={adminClubId}
                    onChange={(e) => {
                      setAdminClubId(e.target.value);
                      setEventId("");
                    }}
                  >
                    <option value="">{t("SELECT")}</option>
                    {(clubList || []).map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name || c.slug}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              ) : null}
              <Form.Group>
                <Form.Label>{t("EVENT_PRICING_PICK_EVENT")}</Form.Label>
                <Form.Select
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                >
                  <option value="">{t("SELECT")}</option>
                  {events.map((ev) => {
                    const num = pickEventDisplayNumber(ev);
                    return (
                      <option key={ev._id} value={ev._id}>
                        {num ? `#${num} · ` : ""}
                        {ev.title}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            </div>

            {listBusy ? (
              <div className="eph-loading">
                <Spinner animation="border" />
                <span>{t("EVENT_PRICING_LOADING")}</span>
              </div>
            ) : !selectedEvent ? (
              <div className="eph-empty">
                <i className="bx bx-calendar-star" />
                <strong>{t("EVENT_PRICING_EMPTY_TITLE")}</strong>
                <span>{t("EVENT_PRICING_EMPTY_EVENT")}</span>
              </div>
            ) : (
              <Tab.Container activeKey={hubTab} onSelect={setHubTab}>
                <Nav className="eph-tabs" variant="pills">
                  <Nav.Item>
                    <Nav.Link eventKey="phases">
                      <i className="bx bx-layer" />
                      {t("EVENT_PRICING_TAB_PHASES")}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="hike">
                      <i className="bx bx-trending-up" />
                      {t("EVENT_PRICING_TAB_HIKE")}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="auto">
                      <i className="bx bx-stack" />
                      {t("EVENT_PRICING_TAB_AUTO")}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  <Tab.Pane eventKey="phases">
                    <div className="eph-panel">
                      {tickets.length === 0 ? (
                        <div className="eph-empty">
                          <i className="bx bx-ticket" />
                          <strong>{t("EVENT_PHASES_EMPTY_TIERS")}</strong>
                        </div>
                      ) : (
                        tickets.map((ticket, index) => (
                          <div className="eph-tier" key={ticket._id || index}>
                            <div className="eph-tier-head">
                              <div className="eph-tier-title">
                                <strong>
                                  {ticket.tickettype || t("TICKET_TYPE")}
                                </strong>
                                <span className="eph-pill">
                                  {t("EVENT_PHASES_TIER_SOLD", {
                                    sold: ticket.ticketssold || 0,
                                    total: ticket.totaltickets || 0,
                                  })}
                                </span>
                              </div>
                            </div>
                            <TicketPhaseBuilder
                              ticket={ticket}
                              onChange={(next) => {
                                const copy = [...tickets];
                                copy[index] = next;
                                setTickets(copy);
                              }}
                            />
                          </div>
                        ))
                      )}
                    </div>
                  </Tab.Pane>
                  <Tab.Pane eventKey="hike">
                    <div className="eph-panel">
                      <TablePriceHikePanel
                        hike={hike}
                        onChange={setHike}
                        layout={layout}
                        layoutLoading={layoutLoading}
                        liveIndex={liveIndex}
                        areaPricing={booking.tableBookingAreaPricing}
                        tableBookingEnabled={booking.tableBookingEnabled}
                      />
                    </div>
                  </Tab.Pane>
                  <Tab.Pane eventKey="auto">
                    <div className="eph-panel">
                      <TableAutoPriceHikePanel
                        autoHike={autoHike}
                        onChange={setAutoHike}
                        manualHike={hike}
                        layout={layout}
                        layoutLoading={layoutLoading}
                        liveIndex={liveIndex}
                        areaPricing={booking.tableBookingAreaPricing}
                        tableBookingEnabled={booking.tableBookingEnabled}
                        autoStatus={pickAutoPriceHikeStatus(selectedEvent)}
                      />
                    </div>
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            )}
            <div className="eph-mobile-save">
              <CustomButton
                className="black-btn Fill_Button eventPrimaryBtn"
                type="button"
                title={t("EVENT_PRICING_SAVE")}
                disabled={!selectedEvent || saving}
                loading={saving}
                onClick={onSaveClick}
              />
            </div>
          </Card.Body>
        </Card>
      </div>

      <ConfirmModal
        showModal={confirmKind === "hike"}
        onHide={() => setConfirmKind(null)}
        modalTitle={t("EVENT_HIKE_CONFIRM_TITLE")}
        modalDescription={t("EVENT_HIKE_CONFIRM_BODY", {
          n: remainingHikeTargets,
        })}
        isFirstButton
        isSecondButton
        leftBtnTitle={t("EVENT_HIKE_CONFIRM_APPLY")}
        rightBtnTitle={t("CANCEL")}
        leftBtnClass="ok_Btn"
        rightBtnClass="borderButton"
        onProcced={persist}
        rightButtonClick={() => setConfirmKind(null)}
        loaderButtonShow={saving}
      />
      <ConfirmModal
        showModal={confirmKind === "auto"}
        onHide={() => setConfirmKind(null)}
        modalTitle={t("EVENT_AUTO_HIKE_CONFIRM_TITLE")}
        modalDescription={t("EVENT_AUTO_HIKE_CONFIRM_BODY")}
        isFirstButton
        isSecondButton
        leftBtnTitle={t("EVENT_AUTO_HIKE_CONFIRM_APPLY")}
        rightBtnTitle={t("CANCEL")}
        leftBtnClass="ok_Btn"
        rightBtnClass="borderButton"
        onProcced={persist}
        rightButtonClick={() => setConfirmKind(null)}
        loaderButtonShow={saving}
      />
    </React.Fragment>
  );
};

export default EventPricingHubContainer;
